import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Building2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { getOrganizations, tenantKeys, updateOrganization } from '../tenancy/tenancyApi';
import AuthShell from './AuthShell';

/**
 * One-time onboarding after a first Google sign-in: the account and a default
 * organization already exist — this just lets the user name the organization
 * before entering the app. Fully skippable.
 */
export default function WelcomePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const orgsQuery = useQuery({
    queryKey: tenantKeys.organizations(),
    queryFn: getOrganizations,
  });
  const ownOrg = (orgsQuery.data ?? []).find((o) => o.role === 'admin');

  // Prefill with the auto-generated name once the org loads.
  useEffect(() => {
    if (ownOrg && !name) setName(ownOrg.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownOrg?.id]);

  const saveMutation = useMutation({
    mutationFn: () => updateOrganization({ orgId: ownOrg.id, name: name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.organizations() });
      toast.success('Organization updated');
      navigate('/files', { replace: true });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update the organization')),
  });

  return (
    <AuthShell
      icon={<Building2 className="h-7 w-7 text-brand-600" />}
      title="Name your organization"
      subtitle="Your files, workspaces and members live under it — you can rename it anytime."
    >
      {orgsQuery.isLoading ? (
        <div className="py-6">
          <Spinner />
        </div>
      ) : !ownOrg ? (
        <p className="py-4 text-center text-sm text-slate-500">
          Your organization is ready.{' '}
          <Link to="/files" className="font-medium text-brand-600 hover:underline">
            Continue to your files
          </Link>
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) saveMutation.mutate();
          }}
        >
          <Input
            label="Organization name"
            value={name}
            autoFocus
            maxLength={160}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Inc"
          />
          <Button
            type="submit"
            className="w-full"
            loading={saveMutation.isPending}
            disabled={!name.trim()}
          >
            Continue
          </Button>
          <p className="text-center text-sm">
            <Link to="/files" className="text-slate-500 hover:text-brand-700">
              Skip for now
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
